import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import type { S3Client, PutObjectCommandInput } from '@aws-sdk/client-s3';
import { createS3Client, createCognitoCredentials, loadS3ClientModule } from '../../utils/aws-s3.util';
import { environment } from 'src/environments/environment';
import { DataService } from './data.service';
import { AngularFireAuth } from '@angular/fire/compat/auth';

/**
 * Pulls the useful parts out of an error. AWS SDK v3 errors carry the real
 * reason on $metadata/$fault rather than in .message, so logging only
 * .message throws away the status code and request id you need to tell an
 * IAM denial apart from a network drop.
 */
export function describeError(error: any): string {
  if (!error) return 'unknown error';

  const parts: string[] = [];
  const name = error.name || error.code || error.Code;
  if (name) parts.push(`name=${name}`);

  const status = error.$metadata?.httpStatusCode ?? error.status;
  if (status) parts.push(`http=${status}`);

  if (error.$fault) parts.push(`fault=${error.$fault}`);

  const requestId = error.$metadata?.requestId || error.RequestId;
  if (requestId) parts.push(`requestId=${requestId}`);

  parts.push(`message=${error.message || String(error)}`);

  // The S3 SDK's browser HTTP handler is a bare `fetch()` call with no
  // wrapping — a rejection reaches here exactly as the browser threw it. A
  // generic "TypeError: network error" collapses several distinct causes
  // (CORS, DNS, a body Blob/File that failed to read, an aborted request)
  // into identical text; `cause` is where Chrome/Firefox put the specific
  // reason, when they set one. Without this, that detail was silently
  // dropped on every network-error log line.
  if (error.cause) {
    parts.push(`cause=${describeError(error.cause)}`);
  }

  return parts.join(' ');
}

@Injectable({
  providedIn: 'root'
})
export class LogToS3Service {
  private logs: any[] = [];
  private inactivityTimer: any;
  private readonly inactivityDuration = 1 * 60 * 1000; // 1 minute
  private readonly MAX_LOGS = 1000; // Maximum number of logs to keep in localStorage
  private readonly UPLOAD_THRESHOLD = 400; // Flush to S3 once this many logs pile up
  private saveTimer: any = null; // Debounces the localStorage write
  private readonly SAVE_DEBOUNCE = 2000;
  private isUploading = false; // Prevent concurrent uploads
  private uploadRetries = 0;
  private readonly MAX_RETRIES = 3;
  userId: any;
  private identityPoolId: string = environment.aws.IdentityPoolId;
  private s3Client: S3Client | null = null;
  private credentialsExpiryTime = 0;

  /**
   * Bucket the log files go to. Logs live at pwa_logs/{userId}/ — a top-level
   * prefix deliberately outside uploadFolder ('pwa'), so whatever S3 trigger
   * ingests uploaded media never sees these .txt files.
   */
  private get logBucket(): string {
    return (environment.aws as any).logBucket || environment.aws.bucketName;
  }
  constructor(private http: HttpClient, private router: Router, //private swUpdate: SwUpdate,
    private ngZone: NgZone, public dataService: DataService, public fbAuth: AngularFireAuth
  ) {



    // Pick up anything left over from a previous session BEFORE the first
    // log() call. Without this, this.logs starts empty and the first
    // saveLogsToLocalStorage() overwrites the persisted logs we were keeping
    // for retry, so a failed upload could never be recovered.
    this.loadLogsFromLocalStorage();

    // this.clearLogs();
    // Log navigation events
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.log(`User navigated to ${event.url}`);
      }
    });

    // Sync logs when the app comes online
    // this.swUpdate.activated.subscribe(() => {
    //   this.syncLogs();
    // });

    // beforeunload is too late to finish an async S3 PUT (and never fires at
    // all when a mobile tab is discarded), so the real flush happens when the
    // page is hidden; beforeunload only persists to localStorage.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushLogsToLocalStorage();
        this.uploadLogsToS3();
      }
    });

    // visibilitychange is not guaranteed to fire before a mobile browser tears
    // a page down or freezes it. In a real session the log went silent for 54s
    // around the moment the user left the upload's window (the server saw the
    // page polling the whole time), and the lines from that stretch never
    // reached S3 — the stretch that mattered. These two fire at the last
    // moment the page can still run anything.
    window.addEventListener('pagehide', () => {
      this.flushLogsToLocalStorage();
      this.uploadLogsToS3();
    });
    document.addEventListener('freeze', () => {
      this.flushLogsToLocalStorage();
      this.uploadLogsToS3();
    });

    window.addEventListener('beforeunload', () => {
      this.syncLogsBeforeUnload();
    });

    // Also try to upload periodically to avoid losing logs
    setInterval(() => {
      if (this.logs.length >= 50) { // Upload when we have 50+ logs
        this.uploadLogsToS3();
      }
    }, 60000); // Check every minute

    // Start tracking user activity
    this.startInactivityTimer();
  }

  // Persist (and best-effort upload) on page unload
  private syncLogsBeforeUnload() {
    if (this.logs.length === 0) return;

    try {
      // Save to localStorage — this is the part that reliably completes.
      // The upload itself is handled by the visibilitychange flush above, or
      // by the next session picking these logs back up.
      this.flushLogsToLocalStorage();

      this.uploadLogsToS3().catch(() => {
        // Logs will persist in localStorage for next session
      });
    } catch (error) {
      // console.warn('Error in beforeunload log sync:', error); //__CONSOLE_LOG__
    }
  }


  logNavigation(url: string) {
    this.log(`User navigated to ${url}`);
  }

  getDeviceType() {
    const userAgent = navigator.userAgent.toLowerCase();
    const isMobile = /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
    //  console.log('isMobile:', isMobile ? 'Mobile' : 'Desktop');
    if (userAgent.match(/ipad|iphone|ipod/)) {
      // console.log('iOS'); //__CONSOLE_LOG__
    }
    return isMobile ? 'Mobile' : 'Desktop';
  }

  getNetworkSpeed() {
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      return {
        downlink: connection.downlink,
        effectiveType: connection.effectiveType, // 3g, 4g, etc.
        rtt: connection.rtt,
        saveData: !!connection.saveData,
      };
    } else {
      return { downlink: null, effectiveType: 'unknown', rtt: null, saveData: false };
    }
  }

  // One line per entry, not nine — the previous box format repeated the full
  // User Agent, Device, Network Status and Network Speed block on every
  // single line (identical on ~all of them), and printed a User ID that was
  // never actually populated (log entries never carried a `user` field, so
  // it always rendered "N/A"). UA/device now go once in the file header
  // (see createLogFile); this keeps only what varies per line. Visibility
  // state is included because a backgrounded tab is exactly the kind of
  // thing that explains an otherwise-unexplained failure a few lines later.
  formatLog(log: any): string {
    if (!log) return 'Invalid log entry';

    const net = log.networkSpeed || {};
    const netStr = `${log.network?.status || 'unknown'}/${net.effectiveType || 'unknown'} `
      + `${net.downlink ?? '?'}Mbps ${net.rtt ?? '?'}ms${net.saveData ? ' saveData' : ''}`;
    const visStr = log.visibility && log.visibility !== 'visible' ? ` vis=${log.visibility}` : '';
    const level = log.level === 'error' ? '[ERROR] ' : '';

    return `${log.timestamp || 'N/A'} ${level}${log.message || 'N/A'} | net=${netStr}${visStr}`;
  }

  // Log a message
  log(message: string, level: 'info' | 'error' = 'info') {
    const logEntry = {
      timestamp: new Date().toISOString(), message, level,
      network: { status: navigator.onLine ? 'online' : 'offline' }, networkSpeed: this.getNetworkSpeed(),
      visibility: document.visibilityState,
    };
    this.logs.push(logEntry);

    // Flush before we hit MAX_LOGS, otherwise the trim below silently discards
    // logs that were never uploaded — which is exactly what happened during a
    // bulk upload, where a single batch produces thousands of entries.
    if (this.logs.length >= this.UPLOAD_THRESHOLD) {
      this.uploadLogsToS3();
    }

    // Limit logs array size to prevent memory issues
    if (this.logs.length > this.MAX_LOGS) {
      this.logs = this.logs.slice(-this.MAX_LOGS); // Keep only the last MAX_LOGS entries
    }

    this.mirrorToConsole('log', message);
    this.scheduleSaveToLocalStorage();
    this.resetInactivityTimer(); // Reset the timer on user activity
  }

  /**
   * Log a failure. Always reaches the console, even in production and even
   * when the debug mirror is off — a failed upload is exactly the thing we
   * can't afford to only find out about from an S3 file that may not arrive.
   */
  logError(message: string, error?: any) {
    const detail = error !== undefined ? ` | ${describeError(error)}` : '';
    this.log(`${message}${detail}`, 'error');
    this.writeToConsole('error', `${message}${detail}`, error);
  }

  /**
   * True when every log line should also go to the console. Read once per
   * session (log() runs thousands of times during an upload, so this must not
   * hit localStorage each call) — flipping the flag needs a reload.
   */
  private consoleMirror: boolean | null = null;
  private consoleMirrorEnabled(): boolean {
    if (this.consoleMirror === null) {
      let flag: string | null = null;
      try {
        flag = localStorage.getItem('debugLogs');
      } catch { /* no-op */ }
      this.consoleMirror = flag === 'true' ? true : flag === 'false' ? false : !environment.production;
    }
    return this.consoleMirror;
  }

  private mirrorToConsole(level: 'log' | 'error', message: string) {
    if (!this.consoleMirrorEnabled()) return;
    this.writeToConsole(level, message);
  }

  /**
   * NOTE: written as a dynamic lookup on purpose. remove-logs.js comments out
   * any line that starts with `console.log|warn|error(` during build:prod, so
   * a direct call here would be stripped out of the production bundle.
   */
  private writeToConsole(level: 'log' | 'error', message: string, error?: any) {
    try {
      const sink: any = console;
      const fn = sink[level] || sink.log;
      if (error !== undefined) {
        fn.call(sink, `[picaggo] ${message}`, error);
      } else {
        fn.call(sink, `[picaggo] ${message}`);
      }
    } catch { /* no-op */ }
  }

  // Writing on every log() means re-stringifying the whole array each time,
  // which is O(n^2) over a noisy burst. Coalesce into one write per 2s.
  private scheduleSaveToLocalStorage() {
    if (this.saveTimer) return;
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      this.saveLogsToLocalStorage();
    }, this.SAVE_DEBOUNCE);
  }

  // Write immediately, cancelling any pending debounced write
  private flushLogsToLocalStorage() {
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    this.saveLogsToLocalStorage();
  }

  // Save logs to localStorage
  private saveLogsToLocalStorage() {
    try {
      localStorage.setItem('logs', JSON.stringify(this.logs));
    } catch (e) {
      // If quota exceeded, upload existing logs and clear
      if (e instanceof DOMException && (e.name === 'QuotaExceededError' || e.code === 22)) {
        // console.warn('localStorage quota exceeded, uploading and clearing logs'); //__CONSOLE_LOG__
        this.uploadLogsToS3();
        this.clearLogs();
        // Try again with fresh logs array
        try {
          localStorage.setItem('logs', JSON.stringify(this.logs));
        } catch (retryError) {
          // console.error('Still unable to save logs after clearing'); //__CONSOLE_LOG__
        }
      }
    }
  }

  // Load logs from localStorage
  private loadLogsFromLocalStorage() {
    const logs = localStorage.getItem('logs');
    this.logs = logs ? JSON.parse(logs) : [];
  }

  // Create a Blob (file) from logs. `this.userId` must already be resolved
  // by the caller — it's what identifies the file (both the header below and
  // the pwa_logs/{userId}/ key it gets uploaded under).
  private createLogFile(entries: any[] = this.logs, owner: string = this.userId): Blob {
    if (!Array.isArray(entries) || entries.length === 0) {
      return new Blob(["No logs available"], { type: 'text/plain' });
    }

    // Written once per file instead of once per line (see formatLog).
    const header = `user=${owner || 'unknown'} device=${this.getDeviceType()} ua="${navigator.userAgent}"\n`;
    const formattedLogs = entries.map(log => this.formatLog(log)).join("\n");
    return new Blob([header + formattedLogs], { type: 'text/plain' });
  }

  // sendLogsToS3() {
  //   // if (this.logMessages.length === 0) return;

  //   // const logContent = this.logMessages.join('\n');
  //   const logFile = this.createLogFile(logs);
  //   const filename = `logs-${new Date().toISOString()}.txt`;

  //   const params = {
  //     Bucket: 'test-picaggo',
  //     Key: filename, // Name of the log file
  //     Body: logFile
  //   };

  //   this.s3.upload(params, (err: any, data: { Location: any; }) => {
  //     if (err) {
  //       console.error('Error uploading logs to S3:', err);
  //     } else {
  //       console.log('Logs uploaded to S3:', data.Location);
  //     }
  //   });
  // }

  // Initialize AWS credentials with caching
  private async initializeAWSCredentials(): Promise<S3Client> {
    // Check if credentials are still valid (expire in 1 hour, refresh 5 min early)
    const now = Date.now();
    if (this.s3Client && this.credentialsExpiryTime > now + 300000) {
      return this.s3Client;
    }

    const currentUser = await this.fbAuth.currentUser;
    if (!currentUser) {
      throw new Error("No authenticated user found for logging.");
    }

    const firebaseToken = await currentUser.getIdToken(true);

    const region = 'us-west-1';

    // Create reusable S3 client with Cognito Identity Pool credentials.
    // The provider fetches/refreshes credentials lazily on the first request.
    this.s3Client = await createS3Client({
      region,
      credentials: await createCognitoCredentials({
        identityPoolId: this.identityPoolId,
        logins: {
          'securetoken.google.com/picaggo-235807': firebaseToken
        },
        clientConfig: { region }
      })
    });

    // Set expiry time (Cognito credentials valid for 1 hour)
    this.credentialsExpiryTime = Date.now() + (60 * 60 * 1000);

    return this.s3Client;
  }

  /** One forced upload per page load, so a failing batch cannot spam the bucket. */
  private failureLogSent = false;

  private isGuestSession(): boolean {
    try {
      return localStorage.getItem('isGuestUser') === 'true' || sessionStorage.getItem('isGuestUser') === 'true';
    } catch {
      return false;
    }
  }

  /**
   * Send the log now because an upload went wrong, guests included.
   *
   * Guests never upload logs, which left every guest upload failure visible
   * only as PostHog events — the S3 log that explains the PostHog event simply
   * did not exist. A failure is the one moment worth the exception. Once per
   * page load, and only the lines already in the buffer.
   */
  sendFailureLog(reason: string): void {
    if (this.failureLogSent) return;
    this.failureLogSent = true;
    this.log(`[FailureLog] Sending log because: ${reason}`);
    this.uploadLogsToS3({ force: true });
  }

  // Upload logs to S3 with proper error handling and retry logic
  async uploadLogsToS3(opts: { force?: boolean } = {}): Promise<void> {
    // Prevent concurrent uploads
    if (this.isUploading) {
      // console.log('Log upload already in progress, skipping...'); //__CONSOLE_LOG__
      return;
    }

    // Do not upload logs for guest users — except when a failure asked for it
    // (see sendFailureLog).
    if (!opts.force && this.isGuestSession()) {
      return;
    }

    this.isUploading = true;

    try {
      // In-memory is authoritative (the constructor seeds it from
      // localStorage); only fall back to disk if we have nothing, so a
      // debounced write in flight can't cost us the newest entries.
      if (this.logs.length === 0) {
        this.loadLogsFromLocalStorage();
      }

      if (this.logs.length === 0) {
        // console.log('No logs to upload.'); //__CONSOLE_LOG__
        this.isUploading = false;
        return;
      }

      // Snapshot what we're sending so logs written during the upload survive
      const batch = this.logs.slice();

      // Get S3 client with fresh credentials
      const s3 = await this.initializeAWSCredentials();

      // localStorage 'userId' is never actually written (both setItem calls are
      // commented out) and dataService.userId is only set once event-detail or
      // gallery has run, so fall back to the Firebase uid — otherwise these
      // land under pwa_logs/undefined/ where nobody thinks to look. Resolved
      // before createLogFile() below, since its header needs it too — it
      // used to run after, so the header/key could disagree if this were
      // read there.
      this.userId = this.userId
        || this.dataService.userId
        || localStorage.getItem('userId')
        || (await this.fbAuth.currentUser)?.uid
        || 'unknown';

      // A forced guest log gets its own folder. `this.userId` can be the host's
      // id for a guest (dataService.userId is the album owner's), and one real
      // log folder already ended up holding two people's uploads for that
      // reason; mixing a guest's failure into the host's folder would make it
      // read as the host's.
      let owner: string = this.userId;
      if (opts.force && this.isGuestSession()) {
        owner = `guest-${(await this.fbAuth.currentUser)?.uid || 'unknown'}`;
      }

      const logFile = this.createLogFile(batch, owner);
      const filename = `logs-${new Date().toISOString()}.txt`;

      const bucket = this.logBucket;
      if (!bucket) throw new Error('S3 bucket not configured');

      const params: PutObjectCommandInput = {
        Bucket: bucket,
        Key: `pwa_logs/${owner}/${filename}`,
        Body: logFile,
        ContentType: 'text/plain'
      };

      const { PutObjectCommand } = await loadS3ClientModule();
      await s3.send(new PutObjectCommand(params));

      // console.log(`Logs uploaded successfully to S3: ${params.Key}`); //__CONSOLE_LOG__

      // Drop only what we actually sent; anything logged during the upload stays
      this.logs = this.logs.slice(batch.length);
      this.flushLogsToLocalStorage();
      localStorage.removeItem('lastLogUploadError');
      this.uploadRetries = 0; // Reset retry counter

    } catch (error: any) {
      const errorMsg = describeError(error);
      this.writeToConsole('error', `Log upload to S3 failed (bucket=${this.logBucket}) | ${errorMsg}`, error);

      // Console output is stripped in this app, so leave the reason somewhere
      // inspectable — an S3 403 here means the Cognito role can't write the
      // pwa_logs prefix, which is otherwise completely invisible.
      try {
        localStorage.setItem('lastLogUploadError', `${new Date().toISOString()} ${errorMsg}`);
      } catch { /* no-op */ }

      // Retry logic
      this.uploadRetries++;
      if (this.uploadRetries < this.MAX_RETRIES) {
        // console.log(`Retrying log upload (attempt ${this.uploadRetries}/${this.MAX_RETRIES}) //__CONSOLE_LOG__...`);
        // Keep logs in localStorage for retry
        this.saveLogsToLocalStorage();

        // Retry after exponential backoff
        const delay = 1000 * Math.pow(2, this.uploadRetries);
        setTimeout(() => {
          this.isUploading = false;
          this.uploadLogsToS3(opts);
        }, delay);
      } else {
        // console.error('Max retries reached for log upload. Logs will be preserved for next session.'); //__CONSOLE_LOG__
        // Keep logs in localStorage for next session
        this.saveLogsToLocalStorage();
        this.uploadRetries = 0;
      }
    } finally {
      if (this.uploadRetries >= this.MAX_RETRIES || this.uploadRetries === 0) {
        this.isUploading = false;
      }
    }
  }

  // Clear logs
  private clearLogs() {
    this.logs = [];
    localStorage.removeItem('logs');
  }

  // Sync logs when the app comes online
  private syncLogs = () => {
    if (navigator.onLine) {
      this.uploadLogsToS3();
    }
  }


  // Start inactivity timer
  private startInactivityTimer() {
    this.ngZone.runOutsideAngular(() => {
      const reset = this.resetInactivityTimer.bind(this);
      window.addEventListener('mousemove', reset);
      window.addEventListener('keypress', reset);
      window.addEventListener('click', reset);
      window.addEventListener('touchstart', reset, { passive: true });
      window.addEventListener('touchmove', reset, { passive: true });
      this.resetInactivityTimer();
    });
  }

  // Reset inactivity timer
  private resetInactivityTimer() {
    clearTimeout(this.inactivityTimer);
    this.inactivityTimer = setTimeout(() => {
      this.ngZone.run(() => {
        this.uploadLogsToS3(); // Upload logs after inactivity
      });
    }, this.inactivityDuration);
  }

  // ngOnDestroy() {
  //   this.sendLogsToS3();
  //   window.removeEventListener('beforeunload', this.sendLogsToS3.bind(this));
  // }
}
